import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, KeyRound, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { BrandMark } from "../components/BrandMark";
import { api } from "../lib/api";

/**
 * Dedicated staff sign-in page.
 * Only shows the email + password form. No Google OAuth, no SMS OTP.
 * The consumer Login page (/login) is strictly for customers.
 */
export default function ManagerLogin() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // If a user with a staff/admin role is already signed in, send them straight to the Back Office.
  if (!loading && user && (user.role === "staff" || user.role === "admin")) {
    const from = location.state?.from || "/backoffice";
    window.location.replace(from);
    return null;
  }

  const submit = async (e) => {
    e?.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password-login", { email, password });
      if (data?.session_token) localStorage.setItem("pr_session_token", data.session_token);
      const role = data?.user?.role;
      if (role !== "staff" && role !== "admin") {
        toast.error("This sign-in is for staff only. Customers, please use the main sign-in.");
        localStorage.removeItem("pr_session_token");
        setBusy(false);
        return;
      }
      toast.success("Welcome back");
      window.location.replace(location.state?.from || "/backoffice");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign in");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="manager-login-page"
      className="mx-auto max-w-md px-5 sm:px-8 pt-24 pb-24">
      <div className="glass rounded-3xl p-8 text-center border border-electric/30">
        <div className="inline-flex items-center gap-2 label-caps text-electric text-[10px] mb-4">
          <ShieldCheck size={14} /> Staff · Back Office
        </div>
        <BrandMark size={56} />
        <h1 className="font-serif text-3xl mt-6 mb-2">Manager sign-in</h1>
        <p className="text-white/60 text-sm mb-8">
          Enter the email and password provided to you by the owner. This portal is private
          — customers should <Link to="/login" className="text-cream underline">use the main sign-in</Link>.
        </p>

        <form onSubmit={submit} className="space-y-4 text-left">
          <label className="block">
            <div className="label-caps text-white/60 mb-2">Work email</div>
            <input
              data-testid="mgr-login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@plenty-rydez.com"
              className="w-full bg-ink-800 border border-white/10 rounded-xl px-4 py-3 focus:border-electric outline-none"
              autoComplete="email"
              required
            />
          </label>
          <label className="block">
            <div className="label-caps text-white/60 mb-2">Password</div>
            <input
              data-testid="mgr-login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full bg-ink-800 border border-white/10 rounded-xl px-4 py-3 focus:border-electric outline-none"
              autoComplete="current-password"
              required
              minLength={8}
            />
          </label>
          <button
            data-testid="mgr-login-submit"
            type="submit"
            disabled={busy}
            className="btn-burgundy w-full px-6 py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="text-[11px] text-white/40 mt-6">
          Don&apos;t have credentials? Ask the owner to create your account from the Team tab.
        </p>
      </div>
    </section>
  );
}
